import { GetStoreMetadata } from '@/data';
import {
  BrandPageDocument,
  DealsPageDocument,
  KioskMainMenuDocument,
  ProductGroupPageDocument,
  ProductListingPageDocument,
  SearchPageDocument,
  StrainPageDocument,
  SubcategoryPageDocument,
} from '@/types';
import {
  DEFAULT_PRISMIC_SORT_FILTER,
  HEADLESS_TO_OPENSEARCH_MAP,
} from 'constants/treez_filters';

/** Combines the search parameters with the default sorting option from the headless CMS.
 * If the 'sort' parameter is not present in the search parameters, adds the default sort for the page.
 * @param searchParams - URL search parameters
 * @param defaultSort - Default sort value from the headless CMS (subCategoryPage?.data.sort_products_on_page)
 * @returns Updated searchParams with the appropriate sort option */

export const resolveSearchParams = (
  searchParams: Record<string, string | string[]>,
  defaultSort?: SortOptionalValue,
): Record<string, string | string[]> => {
  // If a sort is already present, just return as is
  if (searchParams.sort) {
    return searchParams;
  }

  const defaultSortParam = convertPrismicSortToOpenSearch(
    defaultSort as SortOptionalValue,
  );

  if (defaultSortParam) {
    return {
      ...searchParams,
      sort: defaultSortParam,
    };
  }

  return searchParams;
};

/**
 * Convert the Prismic sort_products_on_page values
 * to the format OpenSearch understands.
 */
export const convertPrismicSortToOpenSearch = (
  prismicSortValue?: SortOptionalValue,
): string => HEADLESS_TO_OPENSEARCH_MAP[prismicSortValue ?? '']?.sort;

/**
 * Retrieves the value of `sort_products_on_page` from the Prismic document.
 * If the field doesn't exist or an error occurs, it returns 'Price: Low to High'.
 */
export const getSortWithFallback = async (
  document?: DocumentWithSort,
  defaultDocument?: DocumentWithSort,
): Promise<SortOptionalValue> => {
  // 1: Verify main document
  if (document && document?.data?.sort_products_on_page) {
    return document.data.sort_products_on_page as SortOptionalValue;
  }

  // 2: verify defaultDocument
  if (defaultDocument && defaultDocument?.data?.sort_products_on_page) {
    return defaultDocument.data.sort_products_on_page as SortOptionalValue;
  }

  const { settings } = await GetStoreMetadata();

  // 3: Obtain default sort from settings or use default
  return (
    (settings?.sort_products_on_page as SortOptionalValue) ||
    DEFAULT_PRISMIC_SORT_FILTER
  );
};

type DocumentWithSort =
  | ProductListingPageDocument
  | StrainPageDocument
  | SubcategoryPageDocument
  | DealsPageDocument
  | SearchPageDocument
  | KioskMainMenuDocument
  | ProductGroupPageDocument
  | BrandPageDocument
  | null;

export type SortOptionalValue =
  | 'Price Low to High'
  | 'Price High to Low'
  | 'THC Low to High'
  | 'THC High to Low'
  | 'Classification A–Z'
  | 'Classification Z–A'
  | 'Brand A–Z'
  | 'Brand Z–A'
  | 'Expiration Date (Soonest First)'
  | 'Expiration Date (Latest First)'
  | 'Product Creation Date (Oldest to Newest)'
  | 'Product Creation Date (Newest to Oldest)'
  | 'Random Order';
